/*
 * useMapRecenter
 *
 * Single owner of map-recenter decisions: every recenter goes through one of
 * the intents returned here; RecenterAutomaticallyMap applies the resulting
 * centerLatLonMap to the Leaflet map.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  buildVesselsGeojson,
  vesselRecenterPoint,
} from "@/utils/vesselHelper";
import { nowMs } from "@/utils/appNow";

dayjs.extend(utc);

const isFiniteNum = (n) => Number.isFinite(Number(n));

export default function useMapRecenter({ lastPositionByVesselId } = {}) {
  const [centerLatLonMap, setCenterLatLonMap] = useState([30, -45]);

  const recenterToPoint = useCallback((latlng) => {
    if (!Array.isArray(latlng) || latlng.length !== 2) return;
    const [lat, lng] = latlng;
    if (!isFiniteNum(lat) || !isFiniteNum(lng)) return;
    setCenterLatLonMap([Number(lat), Number(lng)]);
  }, []);

  const lastPositionRef = useRef(lastPositionByVesselId);
  lastPositionRef.current = lastPositionByVesselId;
  const recenterToVesselLastPosition = useCallback(
    (vesselId) => {
      const lonlat = lastPositionRef.current?.[Number(vesselId)];
      if (!Array.isArray(lonlat) || lonlat.length !== 2) return;
      recenterToPoint([lonlat[1], lonlat[0]]);
    },
    [recenterToPoint],
  );

  // Single-vessel accounts start centered on their vessel, once per login.
  const autoCenteredRef = useRef(false);
  useEffect(() => {
    const ids = Object.keys(lastPositionByVesselId ?? {});
    if (ids.length === 0) {
      autoCenteredRef.current = false;
      return;
    }
    if (autoCenteredRef.current) return;
    autoCenteredRef.current = true;
    if (ids.length === 1) recenterToVesselLastPosition(ids[0]);
  }, [lastPositionByVesselId, recenterToVesselLastPosition]);

  const [routeRecenterRequest, setRouteRecenterRequest] = useState({
    nonce: 0,
    routeId: null,
  });
  const recenterToRoute = useCallback(
    (routeId = null) =>
      setRouteRecenterRequest((prev) => ({ nonce: prev.nonce + 1, routeId })),
    [],
  );

  return {
    centerLatLonMap,
    recenterToPoint,
    recenterToVesselLastPosition,
    recenterToRoute,
    routeRecenterRequest,
  };
}

// Resolves recenterToRoute requests to the route's interpolated position at
// the slider time. Separate hook: its inputs exist later in MainComponent.
export function useRouteRecenterResolver({
  routeRecenterRequest,
  selectedPlanRoutes,
  draftRoutes,
  sliderPosition,
  recenterToPoint,
}) {
  const lastNonceRef = useRef(routeRecenterRequest?.nonce ?? 0);
  useEffect(() => {
    const { nonce, routeId } = routeRecenterRequest ?? {};
    if (nonce == null || nonce === lastNonceRef.current) return;

    // Request stays unconsumed until the clicked route's geometry is loaded.
    const idEq = (a, b) => String(a) === String(b);
    const route =
      (selectedPlanRoutes ?? []).find((r) =>
        idEq(r?.points?.routeId, routeId),
      ) ??
      (draftRoutes ?? []).find((r) => idEq(r?.points?.routeId, routeId)) ??
      null;
    if (!route) return;

    // Same interpolation as the icon — the nearest waypoint can sit far off.
    const sliderMs = dayjs.utc(sliderPosition?.label).valueOf();
    let coords = null;
    if (Number.isFinite(sliderMs)) {
      const [vessel] = buildVesselsGeojson({ routes: [route], sliderPosition });
      coords =
        vessel?.points?.geojson?.features?.[0]?.geometry?.coordinates ?? null;
    }
    if (!coords) {
      coords = vesselRecenterPoint(
        route,
        Number.isFinite(sliderMs) ? sliderMs : nowMs(),
      );
    }
    if (
      Array.isArray(coords) &&
      isFiniteNum(coords[0]) &&
      isFiniteNum(coords[1])
    ) {
      lastNonceRef.current = nonce;
      recenterToPoint([Number(coords[1]), Number(coords[0])]);
    }
  }, [
    routeRecenterRequest,
    selectedPlanRoutes,
    draftRoutes,
    sliderPosition,
    recenterToPoint,
  ]);
}
